import axios from '@/plugins/axios';
import utils from '@/stores/utils';

const state = {
    billing_stats: null,

    evolution: null,
    evolutionPeriod: "last12months",
    evolutionLoading: false,
    evolutionLoadingErrors: {},
    evolutionCancelSource: null,

    byCustomer: null,
    byCustomerPeriod: "last12months",
    byCustomerLoading: false,
    byCustomerLoadingErrors: {},
    byCustomerCancelSource: null,

    providerEvolution: null,
    providerEvolutionPeriod: "last12months",
    providerEvolutionLoading: false,
    providerEvolutionLoadingErrors: {},
    providerEvolutionCancelSource: null,

    byProvider: null,
    byProviderPeriod: "last12months",
    byProviderLoading: false,
    byProviderLoadingErrors: {},
    byProviderCancelSource: null,

    statsLoading: false,
    statsLoadingErrors: {},
    statsCancelSource: null,
};

const mutations = {

    updateStatsLoading(state, statsLoading) {
        state.statsLoading = statsLoading;
        state.statsLoadingErrors = {};
    },
    updateStatsLoadingErrors(state, statsLoadingErrors) {
        state.statsLoadingErrors = statsLoadingErrors;
        state.statsLoading = false;
    },

    updateStats(state, stats) {
        state.statsLoading = false;
        state.billing_stats = stats;
    },

    updateEvolution(state, evolution) {
        state.evolution = evolution;
        state.evolutionLoading = false;
        state.evolutionLoadingErrors = {};
    },
    updateEvolutionPeriod(state, evolutionPeriod) {
        state.evolution = null;
        state.evolutionPeriod = evolutionPeriod;
    },
    updateEvolutionLoading(state, evolutionLoading) {
        state.evolutionLoading = evolutionLoading;
    },
    updateEvolutionLoadingErrors(state, evolutionLoadingErrors) {
        state.evolutionLoading = false;
        state.evolutionLoadingErrors = evolutionLoadingErrors;
    },

    updateByCustomerPeriod(state, byCustomerPeriod) {
        state.byCustomer = null;
        state.byCustomerPeriod = byCustomerPeriod;
    },
    updateByCustomer(state, byCustomer) {
        state.byCustomer = byCustomer;
        state.byCustomerLoading = false;
        state.byCustomerLoadingErrors = {};
    },
    updateByCustomerLoading(state, byCustomerLoading) {
        state.byCustomerLoading = byCustomerLoading;
    },
    updateByCustomerLoadingErrors(state, byCustomerLoadingErrors) {
        state.byCustomerLoading = false;
        state.byCustomerLoadingErrors = byCustomerLoadingErrors;
    },
    updateStatsCancelSource(state, cancel_source) {
        state.statsCancelSource = cancel_source;
    },
    updateEvolutionCancelSource(state, cancel_source) {
        state.evolutionCancelSource = cancel_source;
    },
    updateByCustomerCancelSource(state, cancel_source) {
        state.byCustomerCancelSource = cancel_source;
    },

    updateProviderEvolution(state, providerEvolution) {
        state.providerEvolution = providerEvolution;
        state.providerEvolutionLoading = false;
        state.providerEvolutionLoadingErrors = {};
    },
    updateProviderEvolutionPeriod(state, providerEvolutionPeriod) {
        state.providerEvolution = null;
        state.providerEvolutionPeriod = providerEvolutionPeriod;
    },
    updateProviderEvolutionLoading(state, providerEvolutionLoading) {
        state.providerEvolutionLoading = providerEvolutionLoading;
    },
    updateProviderEvolutionLoadingErrors(state, providerEvolutionLoadingErrors) {
        state.providerEvolutionLoading = false;
        state.providerEvolutionLoadingErrors = providerEvolutionLoadingErrors;
    },
    updateProviderEvolutionCancelSource(state, cancel_source) {
        state.providerEvolutionCancelSource = cancel_source;
    },

    updateByProviderPeriod(state, byProviderPeriod) {
        state.byProvider = null;
        state.byProviderPeriod = byProviderPeriod;
    },
    updateByProvider(state, byProvider) {
        state.byProvider = byProvider;
        state.byProviderLoading = false;
        state.byProviderLoadingErrors = {};
    },
    updateByProviderLoading(state, byProviderLoading) {
        state.byProviderLoading = byProviderLoading;
    },
    updateByProviderLoadingErrors(state, byProviderLoadingErrors) {
        state.byProviderLoading = false;
        state.byProviderLoadingErrors = byProviderLoadingErrors;
    },
    updateByProviderCancelSource(state, cancel_source) {
        state.byProviderCancelSource = cancel_source;
    },

};

const actions = {

    fetchAllStats({ commit, dispatch, state }, params) {
        // Charts (evolution / bycustomer) are loaded only by the home billing
        // section via setEvolutionPeriod / setByCustomerPeriod. Invoice list and
        // mutation flows only need the summary counters.
        dispatch('fetchStats');
    },

    async fetchStats({ commit, dispatch, state }, params) {
        commit('updateStatsLoading', true);
        
        if (state.statsCancelSource) {
            state.statsCancelSource.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateStatsCancelSource', cancelSource);

        try {
            const response = await axios.get('/api/stats/billing/stats/', {
                cancelToken: state.statsCancelSource?.token
            });
            commit('updateStats', response.data);
            commit('updateStatsCancelSource', null);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateStatsLoadingErrors', error.details);
            throw error;
        }
    },

    async fetchEvolution({ commit, dispatch, state }, params) {
        commit('updateEvolutionLoading', true);
        if (!state.evolutionPeriod) {
            return;
        }

        if (state.evolutionCancelSource) {
            state.evolutionCancelSource.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateEvolutionCancelSource', cancelSource);

        try {
            const response = await axios.get('/api/stats/billing/evolution/', {
                params: { start: state.evolutionPeriod.start, end: state.evolutionPeriod.end },
                cancelToken: state.evolutionCancelSource?.token
            });
            commit('updateEvolution', response.data);
            commit('updateEvolutionCancelSource', null);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateEvolutionLoadingErrors', error.details);
            throw error;
        }
    },

    async fetchByCustomer({ commit, dispatch, state }, params) {
        commit('updateByCustomerLoading', true);
        if (!state.byCustomerPeriod) {
            return;
        }

        if (state.byCustomerCancelSource) {
            state.byCustomerCancelSource.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateByCustomerCancelSource', cancelSource);

        try {
            const response = await axios.get('/api/stats/billing/bycustomer/', {
                params: { start: state.byCustomerPeriod.start, end: state.byCustomerPeriod.end },
                cancelToken: state.byCustomerCancelSource?.token
            });
            commit('updateByCustomer', response.data);
            commit('updateByCustomerCancelSource', null);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateByCustomerLoadingErrors', error.details);
            throw error;
        }
    },

    setByCustomerPeriod({ commit, dispatch, state }, params) {
        commit('updateByCustomerPeriod', params);
        dispatch('fetchByCustomer');
    },

    setEvolutionPeriod({ commit, dispatch, state }, params) {
        commit('updateEvolutionPeriod', params);
        dispatch('fetchEvolution');
    },

    async fetchProviderEvolution({ commit, dispatch, state }, params) {
        commit('updateProviderEvolutionLoading', true);
        if (!state.providerEvolutionPeriod) {
            return;
        }

        if (state.providerEvolutionCancelSource) {
            state.providerEvolutionCancelSource.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateProviderEvolutionCancelSource', cancelSource);

        try {
            const response = await axios.get('/api/stats/billing/provider-evolution/', {
                params: { start: state.providerEvolutionPeriod.start, end: state.providerEvolutionPeriod.end },
                cancelToken: state.providerEvolutionCancelSource?.token
            });
            commit('updateProviderEvolution', response.data);
            commit('updateProviderEvolutionCancelSource', null);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateProviderEvolutionLoadingErrors', error.details);
            throw error;
        }
    },

    setProviderEvolutionPeriod({ commit, dispatch, state }, params) {
        commit('updateProviderEvolutionPeriod', params);
        dispatch('fetchProviderEvolution');
    },

    async fetchByProvider({ commit, dispatch, state }, params) {
        commit('updateByProviderLoading', true);
        if (!state.byProviderPeriod) {
            return;
        }

        if (state.byProviderCancelSource) {
            state.byProviderCancelSource.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateByProviderCancelSource', cancelSource);

        try {
            const response = await axios.get('/api/stats/billing/byprovider/', {
                params: { start: state.byProviderPeriod.start, end: state.byProviderPeriod.end },
                cancelToken: state.byProviderCancelSource?.token
            });
            commit('updateByProvider', response.data);
            commit('updateByProviderCancelSource', null);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateByProviderLoadingErrors', error.details);
            throw error;
        }
    },

    setByProviderPeriod({ commit, dispatch, state }, params) {
        commit('updateByProviderPeriod', params);
        dispatch('fetchByProvider');
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
};
